import { Flex, Button, Heading, HStack, Box, CloseButton, Text } from "@chakra-ui/react";
import { AirhelpPlus } from "@airhelp/react";
import { getDynamicVariable } from "config/dynamicVariables";
import { MAX_GRID_WIDTH } from "config/styleConsts";
import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import { useStickyBottomBarContext } from "contexts/StickyBottomBarContext";
import { ArrowRightIcon } from "styles/icons";
import type { StickyBottomBarProps } from "../StickyBottomBar.d";
import { getCTAElementProps } from "../utils/getCTAElementProps";
import { stickyBottomBarDesktopStyles } from "./StickyBottomBarDesktop.styles";
import { BenefitIcon } from "../components/BenefitIcon";

const StickyBottomBarDesktop = ({ heading, buttonText, benefits, variant }: StickyBottomBarProps) => {
  const language = useLanguage();
  const translations = useTranslations();
  const { closeBottomBar } = useStickyBottomBarContext();
  const CTAButtonProps = getCTAElementProps(language, variant);
  const showBenefits = benefits?.length > 0;
  const designVariant = variant === "basic" ? "basic" : "ahPlus";
  const isAhpVariant = designVariant === "ahPlus";

  const customHeading = {
    basic:
      heading ||
      translations("claim_up_to_compensation", {
        "%compensation%": getDynamicVariable("{{compensation_value}}", language),
      }),
    ahPlus: heading || translations("be_prepared_for_future_flight_disruptions"),
  }[designVariant];

  const buttonContent = {
    basic: buttonText || translations("check_compensation"),
    ahPlus:
      buttonText ||
      translations("from_commission_value_per-year", {
        "%commission_value%": getDynamicVariable("{{ah_essential_price}}"),
      }),
  }[designVariant];

  const button = (
    <Button
      as="a"
      sx={{ svg: { boxSize: 4 }, ...stickyBottomBarDesktopStyles[designVariant].actionButton }}
      {...CTAButtonProps}
    >
      {buttonContent}
      <ArrowRightIcon ml={3} />
    </Button>
  );

  return (
    <Box
      p={{ lg: 6 }}
      color="greyscale.100"
      borderRadius="lg"
      mb={8}
      maxW={MAX_GRID_WIDTH}
      mx={{ lg: 4, xl: "auto" }}
      sx={stickyBottomBarDesktopStyles[designVariant].container}
    >
      <Flex
        position="relative"
        sx={stickyBottomBarDesktopStyles[designVariant].innerContainer}
        flexDir={showBenefits ? "column" : "row"}
      >
        <CloseButton
          position="absolute"
          sx={stickyBottomBarDesktopStyles[designVariant].closeButton}
          onClick={closeBottomBar}
          aria-label="Close bottom bar"
        />
        <AirhelpPlus sx={stickyBottomBarDesktopStyles[designVariant].logo} />
        <Flex sx={stickyBottomBarDesktopStyles[designVariant].headingContainer}>
          <Heading as="h4" variant="h4" sx={stickyBottomBarDesktopStyles[designVariant].heading}>
            {customHeading}
          </Heading>
          {!showBenefits && !isAhpVariant && button}
        </Flex>
        <Flex
          justify="space-between"
          _empty={{ display: "none" }}
          sx={stickyBottomBarDesktopStyles[designVariant].innerSubContainer}
        >
          {showBenefits && (
            <HStack spacing={10} sx={stickyBottomBarDesktopStyles[designVariant].benefits}>
              {benefits.map(({ textKey, text, icon }) => (
                <Flex align="center" key={textKey + text}>
                  <Flex borderRadius="50%" border="2px solid" borderColor="primary.400" mr={2} as="span" p={2}>
                    <BenefitIcon icon={icon} />
                  </Flex>
                  <Text color="white" as="span" fontSize="sm">
                    {(text && text) || translations(textKey)}
                  </Text>
                </Flex>
              ))}
            </HStack>
          )}
          {(showBenefits || isAhpVariant) && button}
        </Flex>
      </Flex>
    </Box>
  );
};

export default StickyBottomBarDesktop;
